import React from 'react';
import {
	GovAccordion,
	GovAccordionItem,
	GovEmpty,
	GovIcon,
	GovSkeleton,
} from '@gov-design-system-ce/react';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import { useRegistrSVDetailContext } from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	ErrorStatusBanner,
	LayoutGap,
} from '@gov-nx/ui/web';
import { CmsPageRender } from '../../../Cms/CmsPageRender';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';
import { RegisterSVDetailSkeleton } from './RegisterSVDetailSkeleton';
import { Table } from './Table';
import { TableChassis } from './TableChassis';
import { TableDimensions } from './TableDimensions';
import { TableMotor } from './TableMotor';
import { TableNoise } from './TableNoise';
import { TableOtherRecords } from './TableOtherRecords';
import { TableOthers } from './TableOthers';
import { TableRelationshipToTheVehicle } from './TableRelationshipToTheVehicle';
import { TableVehicle } from './TableVehicle';
import { TableWeight } from './TableWeight';

export const RegisterSVDetailView = () => {
	const {
		code,
		controls,
		vehicle,
		vehicleGetters,
		rzKeyValues,
		rzHistoryKeyValues,
		tpKeyValues,
		tpHistoryKeyValues,
		orvKeyValues,
		orvHistoryKeyValues,
		technicalInspectionURL,
		isLoading,
	} = useRegistrSVDetailContext();
	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const { t: tsn } = useLocale(code);

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	const getter = vehicle && vehicleGetters(vehicle);

	return (
		<CmsPageRender
			code={code}
			override={{ name: getter?.name() }}
			showDescription={false}>
			{isLoading && <RegisterSVDetailSkeleton />}
			{vehicle && (
				<LayoutGap>
					<ContentLayout>
						<GovAccordion size="l">
							<GovAccordionItem
								open
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-vozidlo')}
									</h2>
								}>
								{getter && (
									<TableVehicle
										tsn={tsn}
										vozidloUdaje={vehicle.vozidloUdaje}
										getter={getter}
									/>
								)}
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-motor')}
									</h2>
								}>
								<TableMotor
									tsn={tsn}
									vehicle={vehicle}
								/>
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-rozmety')}
									</h2>
								}>
								<TableDimensions
									tsn={tsn}
									vehicle={vehicle}
								/>
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-hmotnost')}
									</h2>
								}>
								<TableWeight
									tsn={tsn}
									vehicle={vehicle}
								/>
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-spojovaci-zarizeni')}
									</h2>
								}>
								<TableChassis
									tsn={tsn}
									vehicle={vehicle}
								/>
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-hluk')}
									</h2>
								}>
								<TableNoise
									tsn={tsn}
									vozidloUdaje={vehicle.vozidloUdaje}
								/>
							</GovAccordionItem>
							{/* Dalsi udaje section */}
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-ostatni')}
									</h2>
								}>
								<TableOthers
									tsn={tsn}
									vozidloUdaje={vehicle.vozidloUdaje}
								/>
							</GovAccordionItem>
							{/* Dalsi zaznamy section */}
							{getter?.hasAdditionalRecords && (
								<GovAccordionItem
									label={
										<h2 className="font-bold!">
											{tsn('formular.filtr.detail-dalsi-zaznamy')}
										</h2>
									}>
									<TableOtherRecords />
								</GovAccordionItem>
							)}
						</GovAccordion>
					</ContentLayout>
					<ContentLayout>
						<GovAccordion size="l">
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-dokumenty-rz')}
									</h2>
								}>
								<Table data={rzKeyValues} />
								<Table data={rzHistoryKeyValues} />
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-dokumenty-tp')}
									</h2>
								}>
								<Table data={tpKeyValues} />
								<Table data={tpHistoryKeyValues} />
							</GovAccordionItem>
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-dokumenty-orv')}
									</h2>
								}>
								<Table data={orvKeyValues} />
								<Table data={orvHistoryKeyValues} />
							</GovAccordionItem>
							{getter?.isActive() && (
								<GovAccordionItem
									label={
										<h2 className="font-bold!">
											{tsn('formular.filtr.detail-vozidlo-status')}
										</h2>
									}>
									<Table
										data={[
											[
												tsn('tabulka.dokumenty.vozidlo-status'),
												getter?.statusName(),
											],
										]}
									/>
								</GovAccordionItem>
							)}
							<GovAccordionItem
								label={
									<h2 className="font-bold!">
										{tsn('formular.filtr.detail-vztah-k-vozidlu')}
									</h2>
								}>
								<TableRelationshipToTheVehicle tsn={tsn} />
							</GovAccordionItem>
						</GovAccordion>
					</ContentLayout>
					{getter?.vin && (
						<ContentLayout>
							<LayoutGap
								gap="m"
								className="p-(--spacing-l)">
								{controls.isTechnicalInspectionInfoLoading ? (
									<GovSkeleton
										height="40px"
										width="360px"
										aria-label={t('loading.wcag.processing')}
									/>
								) : (
									<>
										<h2 className="text-body-l-bold!">
											{tsn('formular.nadpis.technicke-prohlidky')}
										</h2>
										<MarkdownRender>
											{tsn('formular.popis.technicke-prohlidky')}
										</MarkdownRender>

										<Button
											onClick={() =>
												window.open(technicalInspectionURL, '_blank')
											}
											color="primary"
											type="outlined"
											size="m"
											aria-label={tsn('akce.wcag.zobrazit-filtr')}
											iconEnd={
												<GovIcon
													name="box-arrow-up-right"
													type="components"
												/>
											}>
											{tsn('formular.odkaz.technicke-prohlidky')}
										</Button>
									</>
								)}
							</LayoutGap>
						</ContentLayout>
					)}
				</LayoutGap>
			)}
			{!vehicle && (
				<LayoutGap>
					<ContentLayout>
						<GovEmpty
							size="l"
							align="center"
							headline={null}
							icon={
								<GovIcon
									type="colored"
									name="empty-file"
									slot="icon"
								/>
							}>
							<p slot="headline">{tsn('chyba.nic-jsme-nenasli')}</p>
							<p>{tsn('chyba.vozidlo-se-nenaslo')}</p>
						</GovEmpty>
					</ContentLayout>
				</LayoutGap>
			)}
		</CmsPageRender>
	);
};
